import styled, { css } from "styled-components";
import { QRCodeCanvas } from "qrcode.react";
import { useGetAccountProfileQuery } from "../../services/accountApi";
import rocketBackgroundImage from "../../../img/rocket.webp";
import { RWD_MD } from "../../utils/rwd";
import Icons from "../../../v2/global/icons";
import { useRef } from "react";
import Logo from "../../../images/logo_light.png";
import { toast, ToastContainer } from "react-toastify";
import { domToCanvas } from "modern-screenshot";
import { useState } from "react";
import { useEffect } from "react";
import { ErrorMySwal } from "../../global/MySwal";
import { useSelector } from "react-redux";
import { selectSystem } from "../../slices/systemSlice";

const StyledWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
`;

const StyledContent = styled.div`
  width: 800px;
  padding: 40px;
  background-image: url(${rocketBackgroundImage});
  background-size: cover;
  position: relative;

  img {
    margin-bottom: 20px;
  }

  ${RWD_MD(css`
    width: 100%;
    height: 100%;
    padding-top: 60px;
  `)};
`;

const StyledList = styled.div`
  display: flex;
  align-items: center;
  font-size: 24px;
  margin-left: 25px;
  margin-bottom: 20px;

  & > div {
    color: #ffffff;
    padding: 0 16px;
    border-right: solid 1px #ffffff;

    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      border: 0;
    }
  }
`;

const StyledAPY = styled.div`
  font-size: 80px;
  margin-bottom: 18px;
  margin-left: 25px;
  color: #19ab70;
  display: flex;
  align-items: center;
  line-height: 1;

  & > div:nth-child(1) {
    align-self: flex-start;
    font-size: 18px;
    color: #ffce56;
  }

  ${RWD_MD(css`
    font-size: 52px;
  `)};
`;

const StyledFlex = styled.div`
  display: flex;
  align-items: center;
  margin-left: 25px;
`;

const StyledQRcode = styled.div`
  width: 80px;
  height: 80px;
  margin-right: 16px;
  border: solid 5px #ffffff;
`;

const StyledReferralContent = styled.div`
  & > div {
    color: #ffffff;

    &:nth-child(1) {
      margin-bottom: 4px;
      font-size: 14px;
    }

    &:nth-child(2) {
      margin-bottom: 4px;
      font-size: 24px;
      font-weight: bold;
    }

    &:nth-child(3) {
      font-size: 16px;
      color: #ffce56;
    }
  }
`;

const StyledCloseButton = styled.div`
  width: 40px;
  height: 40px;
  text-align: center;
  vertical-align: middle;
  color: #ffffff;
  position: absolute;
  right: 16px;
  top: 16px;
  cursor: pointer;

  svg {
    width: 40px;
    height: 40px;
  }
`;

const ShareButton = styled.div`
  border-radius: 60px;
  padding: 0.52vw 1.3vw;
  background: linear-gradient(to bottom, #2f80ed 0%, #5620f0 100%);
  color: #fcfcfc;
  cursor: pointer;
  position: absolute;
  top: calc(100% + 8px);
  right: 0;

  ${RWD_MD(css`
    top: auto;
    bottom: 16px;
    right: 16px;
    padding: 8px 16px;
  `)};
`;

export default function MiningShareModal({
  shareModalData,
  transferDeposit,
  onClose,
}) {
  const lang = useSelector(selectSystem).language;

  const { data } = useGetAccountProfileQuery();

  const [apy, setApy] = useState();

  const ref = useRef(null);

  const calculateAPY = () => {
    const convertTokenUnit = transferDeposit(
      shareModalData.Target,
      shareModalData.PeriodItems
    );
    const cumulativeGrowth = convertTokenUnit / shareModalData.Deposit - 1;

    const now = Date.now();
    const targetDate = new Date(shareModalData.CreateTime).getTime();
    const difference = now - targetDate;
    const daysElapsed = Math.floor(difference / 86400000);

    const annualAPY = ((cumulativeGrowth / daysElapsed + 1) ** 365 - 1) * 100;
    return Number(annualAPY.toFixed(2));
  };

  const handleCopyToClipboard = async () => {
    if (ref.current) {
      try {
        const canvas = await domToCanvas(ref.current);
        canvas.toBlob(async (blob) => {
          if (blob) {
            const item = new ClipboardItem({ "image/png": blob });
            await navigator.clipboard.write([item]);
            toast.success("Image copied!");
          }
        }, "image/png");
      } catch (error) {
        console.error("Failure to copy image", error);
        toast.error(
          "Can't copy the image, please make sure your browser supports it."
        );
      }
    }
  };

  const handleClose = () => {
    setApy(undefined);
    onClose();
  };

  useEffect(() => {
    const apyResult = calculateAPY();

    if (apyResult === Infinity) {
      ErrorMySwal({
        title: "Error",
        text:
          lang.toLowerCase() === "en"
            ? "Data calculation process error, please contact the administrator"
            : "數據計算過程出現錯誤，請聯絡管理員",
      });
    }

    setApy(apyResult);
  }, []);

  return (
    <StyledWrapper onClick={handleClose}>
      <ToastContainer />
      <StyledContent ref={ref} onClick={(e) => e.stopPropagation()}>
        <img src={Logo} />
        <StyledList>
          <div>{shareModalData.PackageType}</div>
          <div>{shareModalData.PackageName}</div>
        </StyledList>
        <StyledAPY>
          <div>APY≈</div>
          <div>{apy > 0 ? "+" : ""}</div>
          {typeof apy === "number" && <div>{`${apy}%`}</div>}
        </StyledAPY>
        <StyledFlex>
          <StyledQRcode>
            <QRCodeCanvas
              value={`${window.location.origin}/v2/mining/addpackage?redirect=/v2/mining/addpackage&asset=${shareModalData.Target}&packageId=${shareModalData.PackageId}`}
              size={70}
            />
          </StyledQRcode>
          <StyledReferralContent>
            <div>Referral Code</div>
            <div>{data.Profile.ReferenceCode}</div>
            <div>Join AVA Mining</div>
          </StyledReferralContent>
        </StyledFlex>
        <ShareButton onClick={handleCopyToClipboard}>Copy Image</ShareButton>
      </StyledContent>
      <StyledCloseButton onClick={handleClose}>
        <Icons.Close_white />
      </StyledCloseButton>
    </StyledWrapper>
  );
}
